/**
 * WidgetFilterModal - Per-widget filter configuration
 *
 * Supports filtering by:
 * - Date range
 * - User role
 * - Status
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Fade,
  Backdrop,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  FilterAlt as FilterIcon,
  RestartAlt as ResetIcon,
} from "@mui/icons-material";
import KDTypography from "components/KDTypography";
import { useDashboard } from "../context/DashboardContext";
import { getWidgetById } from "../config/widgetRegistry";

const DATE_RANGE_OPTIONS = [
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "custom", label: "Custom range" },
];

const ROLE_OPTIONS = [
  { value: "all", label: "All Roles" },
  { value: "admin", label: "Admin" },
  { value: "user", label: "User" },
  { value: "moderator", label: "Moderator" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "suspended", label: "Suspended" },
];

const defaultFilters = {
  dateRange: "30d",
  customStart: "",
  customEnd: "",
  role: "all",
  status: "all",
};

function WidgetFilterModal({ open, onClose, widgetId }) {
  const { state, actions } = useDashboard();
  const widget = getWidgetById(widgetId);
  const existingConfig = state.widgetConfigs?.[widgetId] || {};
  const [filters, setFilters] = useState({ ...defaultFilters, ...existingConfig.filters });

  useEffect(() => {
    if (open) {
      setFilters({ ...defaultFilters, ...existingConfig.filters });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, widgetId]);

  if (!widget) return null;

  const handleChange = (field) => (e) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleApply = () => {
    actions.setWidgetConfig(widgetId, { ...existingConfig, filters });
    onClose();
  };

  const handleReset = () => {
    setFilters({ ...defaultFilters });
  };

  const selectSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: "rgba(255,255,255,0.04)",
      borderRadius: "10px",
      "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
      "&:hover fieldset": { borderColor: "rgba(139, 92, 246, 0.3)" },
      "&.Mui-focused fieldset": { borderColor: "#7C3AED", borderWidth: 1 },
    },
    "& .MuiSelect-select": { color: "white" },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" },
    "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.5)" },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: "rgba(255,255,255,0.04)",
      borderRadius: "10px",
      "& fieldset": { borderColor: "rgba(255,255,255,0.1)" },
      "&:hover fieldset": { borderColor: "rgba(139, 92, 246, 0.3)" },
      "&.Mui-focused fieldset": { borderColor: "#7C3AED", borderWidth: 1 },
    },
    "& .MuiOutlinedInput-input": { color: "white" },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" },
  };

  // Determine which filter sections are relevant for this widget
  const showDateRange = true; // All widgets can filter by date
  const showRoleFilter = widget.category === "users";
  const showStatusFilter = widget.category === "users";

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: { timeout: 200, sx: { backdropFilter: "blur(4px)", bgcolor: "rgba(0,0,0,0.5)" } },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 440,
            bgcolor: "#0F0F1A",
            borderRadius: "20px",
            border: "1px solid rgba(139, 92, 246, 0.2)",
            boxShadow: "0 30px 60px -15px rgba(0,0,0,0.7)",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "linear-gradient(180deg, rgba(139, 92, 246, 0.08) 0%, transparent 100%)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FilterIcon sx={{ color: "white", fontSize: 18 }} />
              </Box>
              <Box>
                <KDTypography variant="subtitle1" sx={{ color: "white", fontWeight: 700, lineHeight: 1.3 }}>
                  Widget Filters
                </KDTypography>
                <KDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
                  {widget.title}
                </KDTypography>
              </Box>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                color: "rgba(255,255,255,0.5)",
                "&:hover": { color: "#EF4444", bgcolor: "rgba(239, 68, 68, 0.1)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Filter fields */}
          <Box sx={{ px: 3, py: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Date range */}
            {showDateRange && (
              <Box>
                <KDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontWeight: 600, mb: 1, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Date Range
                </KDTypography>
                <FormControl fullWidth size="small" sx={selectSx}>
                  <Select value={filters.dateRange} onChange={handleChange("dateRange")}>
                    {DATE_RANGE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {filters.dateRange === "custom" && (
                  <Box sx={{ display: "flex", gap: 1.5, mt: 1.5 }}>
                    <TextField
                      type="date"
                      label="Start"
                      size="small"
                      fullWidth
                      value={filters.customStart}
                      onChange={handleChange("customStart")}
                      InputLabelProps={{ shrink: true }}
                      sx={textFieldSx}
                    />
                    <TextField
                      type="date"
                      label="End"
                      size="small"
                      fullWidth
                      value={filters.customEnd}
                      onChange={handleChange("customEnd")}
                      InputLabelProps={{ shrink: true }}
                      sx={textFieldSx}
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Role filter */}
            {showRoleFilter && (
              <Box>
                <KDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontWeight: 600, mb: 1, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Role
                </KDTypography>
                <FormControl fullWidth size="small" sx={selectSx}>
                  <Select value={filters.role} onChange={handleChange("role")}>
                    {ROLE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Status filter */}
            {showStatusFilter && (
              <Box>
                <KDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontWeight: 600, mb: 1, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Status
                </KDTypography>
                <FormControl fullWidth size="small" sx={selectSx}>
                  <Select value={filters.status} onChange={handleChange("status")}>
                    {STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderTop: "1px solid rgba(255,255,255,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "rgba(0,0,0,0.2)",
            }}
          >
            <Box
              onClick={handleReset}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                color: "rgba(255,255,255,0.5)",
                transition: "color 0.2s",
                "&:hover": { color: "white" },
              }}
            >
              <ResetIcon sx={{ fontSize: 16 }} />
              <KDTypography variant="body2" sx={{ fontSize: "0.8rem" }}>
                Reset
              </KDTypography>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Box
                onClick={onClose}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: "8px",
                  bgcolor: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                }}
              >
                <KDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>
                  Cancel
                </KDTypography>
              </Box>
              <Box
                onClick={handleApply}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  boxShadow: "0 4px 12px rgba(124, 58, 237, 0.4)",
                  "&:hover": { transform: "translateY(-1px)", boxShadow: "0 6px 16px rgba(124, 58, 237, 0.5)" },
                }}
              >
                <KDTypography variant="body2" sx={{ color: "white", fontWeight: 600 }}>
                  Apply Filters
                </KDTypography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}

WidgetFilterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  widgetId: PropTypes.string,
};

export default WidgetFilterModal;
